import {useGetBasicDataItems} from '@/modules/basic-data/api'
import {mapBasicDataItemsToSelectItems} from '@/modules/basic-data/functions'
import {useGetInfiniteProtocols} from '@/modules/protocols/api'
import {useGetPatient} from '@/modules/patients/api/use-get-patient'
import {useGetPatientCharacteristics} from '@/modules/patients/api/use-get-patient-characteristics'
import {useGetPatientLatestGfrLab} from '@/modules/patients/api/use-get-patient-latest-gfr-lab'
import {useGetActivePatientProtocolsWithTreatments} from '@/modules/patients/api/use-get-active-patient-protocols-with-treatments'
import {useGetSettings} from '@/modules/settings/api'
import {useGetProtocol} from '@/modules/protocols/api/use-get-protocol'
import {TherapySequenceTable} from '@/modules/protocols/components/protocol-form/therapy-sequence/therapy-sequence-table'
import {useGetInfiniteDoctors} from '@/modules/users/api'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {DateValue, parseDate} from '@ark-ui/react/date-picker'
import {Box, Button, ButtonGroup, HStack, Text, useDialogContext, VStack, Alert} from '@chakra-ui/react'
import {BasicDataResources} from '@cypro/engine/schema'
import {DialogBody, DialogFooter, DialogHeader} from '@cypro/ui/components'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {useEffect, useMemo, useState} from 'react'
import {uuidv7} from 'uuidv7'
import * as z from 'zod'
import {converTherapySchema} from '@/modules/protocols/functions'
import {
  findOverlappingActiveProtocols,
  formatMinutesToTime,
  getPlannedTreatmentDates,
  parseTimeToMinutes,
} from '@cypro/engine/utils'
import {ActiveProtocolOverlapAlert} from './active-protocol-overlap-alert'

const AddPatientProtocolSchema = z.object({
  protocolId: z.array(z.string()).nonempty(),
  startDate: z.any(),
  diagnosisId: z.array(z.string()),
  cancerTypeId: z.array(z.string()),
  cycles: z.int().min(1),
  startTime: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format (00:00 to 23:59)'),
  responsibleDoctorId: z.string(),
})

function getEarliestTime(sequences: readonly {time: string}[]): string | undefined {
  if (!sequences.length) return undefined
  return sequences.reduce((earliest, seq) => (seq.time < earliest ? seq.time : earliest), sequences[0].time)
}

export interface AddProtocolDialogProps {
  patientId: string
  onAddProtocol: (id: string) => void
}

export function AddProtocolDialog({patientId, onAddProtocol}: AddProtocolDialogProps) {
  const {t} = useTranslate('common')
  const zero = useZero()
  const {settings} = useGetSettings()
  const context = useDialogContext()

  const infiniteResponsibleDoctors = useGetInfiniteDoctors()
  // read the patient here so the generated prescriptions carry the responsible doctor from the About section
  const [patient] = useGetPatient(patientId)

  const [removedSequences, setRemovedSequences] = useState<string[]>([])
  const [characteristics] = useGetPatientCharacteristics(patientId)
  const [gfr] = useGetPatientLatestGfrLab(patientId)
  const [activePatientProtocols] = useGetActivePatientProtocolsWithTreatments(patientId)
  const {items: diagnoses} = useGetBasicDataItems({resource: BasicDataResources.diagnoses, hasProtocols: true})
  const diagnosesItems = mapBasicDataItemsToSelectItems(diagnoses)
  const {items: cancerTypes} = useGetBasicDataItems({resource: BasicDataResources.cancerTypes, hasProtocols: true})
  const cancerTypesItems = mapBasicDataItemsToSelectItems(cancerTypes)

  const startDateDefault: DateValue[] = (() => {
    const d = new Date()
    return [parseDate(d.toISOString().split('T')[0])]
  })()

  const form = useAppForm({
    defaultValues: {
      protocolId: [] as string[],
      startDate: startDateDefault,
      diagnosisId: [] as string[],
      cancerTypeId: [] as string[],
      cycles: undefined as number | undefined,
      startTime: undefined as string | undefined,
      responsibleDoctorId: patient?.responsibleDoctorId ?? '',
    },
    validators: {
      onSubmit: AddPatientProtocolSchema,
    },
    onSubmit({value}) {
      const patientProtocolId = uuidv7()
      return zero.attemptMutation(
        zero.mutators.patients.assignProtocol({
          patientProtocolId,
          patientId,
          protocolId: value.protocolId[0],
          startDate: value.startDate[0].toDate('UTC').getTime(),
          cycles: value.cycles as number,
          startTime: value.startTime as string,
          responsibleDoctorId: value.responsibleDoctorId || null,
          removedSequences: removedSequences,
        }),
        async mutation => {
          const data = await mutation.server
          if (!data) return
          onClose()
          onAddProtocol(patientProtocolId)
          toaster.success({title: t('success'), description: t('patient_updated_successfully')})
        }
      )
    },
  })

  const protocolId = useStore(form.store, state => state.values.protocolId[0])
  const [protocol] = useGetProtocol(protocolId)
  const startTime = useStore(form.store, state => state.values.startTime)
  const isSubmitting = useStore(form.store, state => state.isSubmitting)
  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)
  const diagnosisId = useStore(form.store, state => state.values.diagnosisId)
  const cancerTypeId = useStore(form.store, state => state.values.cancerTypeId)
  const cycles = useStore(form.store, state => state.values.cycles)
  const startDate = useStore(form.store, state => state.values.startDate)

  const infiniteProtocols = useGetInfiniteProtocols({
    filters: {
      diagnosis: diagnosisId?.length ? diagnosisId : undefined,
      cancerTypes: cancerTypeId?.length ? cancerTypeId : undefined,
    },
  })

  const [aucGfrRequired, characteristicsRequired] = useMemo(() => {
    const currentProtocol = protocol
    const required = currentProtocol?.therapySequence?.some(s => {
      return (
        !removedSequences.includes(s.id) &&
        s.medicine.some(m => m?.calculationMethod !== 'ABSOLUTE' && m?.calculationMethod !== 'VOLUME')
      )
    })
    const hasAuc = currentProtocol?.therapySequence?.some(s => {
      return !removedSequences.includes(s.id) && s.medicine.some(m => m?.calculationMethod === 'AUC')
    })
    const aucGfrRequired = hasAuc && !gfr
    const characteristicsRequired = required && !characteristics
    return [aucGfrRequired, characteristicsRequired]
  }, [protocol, removedSequences, characteristics, gfr])

  // Same start date conversion as onSubmit, so the warning checks the dates the prescriptions will get.
  const startDateMs = startDate?.[0]?.toDate('UTC').getTime()

  const protocolOverlaps = useMemo(() => {
    if (!protocol || startDateMs == null) return []
    const newPlannedDates = getPlannedTreatmentDates({
      startDate: startDateMs,
      cycles: cycles && cycles >= 1 ? cycles : protocol.totalCycles,
      daysRepetition: protocol.daysRepetition,
      sequences: protocol.therapySequence,
      removedSequenceIds: removedSequences,
      startTime,
      dayZeroProtocol: settings?.dayZeroProtocol,
    })
    return findOverlappingActiveProtocols({
      enabled: settings?.activeProtocolWarning,
      newPlannedDates,
      patientProtocols: activePatientProtocols,
    })
  }, [protocol, startDateMs, cycles, removedSequences, startTime, settings, activePatientProtocols])

  useEffect(() => {
    setRemovedSequences([])
  }, [protocolId])

  useEffect(() => {
    if (protocol?.totalCycles != null) {
      form.setFieldValue('cycles', protocol.totalCycles, {dontRunListeners: true, dontUpdateMeta: true})
    }
  }, [form, protocol?.totalCycles])

  useEffect(() => {
    const earliestTime = protocol?.therapySequence ? getEarliestTime(protocol.therapySequence) : undefined
    if (earliestTime != null) {
      form.setFieldValue('startTime', earliestTime, {dontRunListeners: true, dontUpdateMeta: true})
    }
  }, [form, protocol?.therapySequence])

  // The patient row can still be loading when the form initializes, so prefill the responsible
  // doctor once it arrives - unless a value is already set or the user has picked one.
  useEffect(() => {
    if (!patient?.responsibleDoctorId) return
    if (form.getFieldValue('responsibleDoctorId')) return
    form.setFieldValue('responsibleDoctorId', patient.responsibleDoctorId, {
      dontRunListeners: true,
      dontUpdateMeta: true,
    })
  }, [form, patient?.responsibleDoctorId])

  function handleRemoveSequence(sequenceId: string) {
    setRemovedSequences(prev => (prev.includes(sequenceId) ? prev : [...prev, sequenceId]))
  }

  function onClose() {
    context.setOpen(false)
  }

  const canSave = !(aucGfrRequired || characteristicsRequired)
  const saveButton = (
    <form.SubmitButton
      loading={isSubmitting}
      disabled={
        isSubmitting ||
        isDefaultValue ||
        aucGfrRequired ||
        characteristicsRequired ||
        removedSequences.length === protocol?.therapySequence.length
      }
    >
      {t('save')}
    </form.SubmitButton>
  )

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <DialogHeader>{t('add_new')}</DialogHeader>

        <DialogBody minHeight="none">
          <VStack spaceY="4" overflow="auto" width="100%" height="60vh">
            <HStack gap="5" width="100%">
              <FormSection variant="card" width={'100%'}>
                <Box display="flex" gap="4">
                  <form.AppField
                    name="diagnosisId"
                    listeners={{
                      onChange: () => {
                        form.setFieldValue('protocolId', [])
                      },
                    }}
                  >
                    {field => (
                      <field.Select
                        label={t('protocol.diagnosis.label')}
                        placeholder={t('protocol.diagnosis.placeholder')}
                        items={diagnosesItems}
                        displayClearTrigger
                      />
                    )}
                  </form.AppField>
                  <form.AppField
                    name="cancerTypeId"
                    listeners={{
                      onChange: () => {
                        form.setFieldValue('protocolId', [])
                      },
                    }}
                  >
                    {field => (
                      <field.Select
                        label={t('protocol.cancer_type.label')}
                        placeholder={t('protocol.cancer_type.placeholder')}
                        items={cancerTypesItems}
                        displayClearTrigger
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="responsibleDoctorId" mode="value">
                    {field => (
                      <field.InfiniteCombobox
                        label={t('doctor_responsible', 'Doctor (responsible)')}
                        placeholder={t('eg', {value: t('name')})}
                        contentProps={{zIndex: 2000}}
                        {...infiniteResponsibleDoctors}
                      />
                    )}
                  </form.AppField>
                </Box>
                <Box display="flex" gap="4">
                  <form.AppField name="protocolId">
                    {field => (
                      <field.InfiniteCombobox
                        label={t('protocol')}
                        placeholder={t('protocol_select.placeholder')}
                        contentProps={{zIndex: 2000}}
                        {...infiniteProtocols}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="cycles">
                    {field => (
                      <field.NumberInput
                        label={t('protocol.total_cycles.label')}
                        placeholder="1"
                        min={1}
                        disabled={!protocolId}
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="startDate">
                    {field => <field.DatePicker label={t('protocol.start_date')} />}
                  </form.AppField>
                  <form.AppField name="startTime">
                    {field => (
                      <field.HourInput
                        label={t('protocol.start_time.label')}
                        placeholder="00:00"
                        disabled={!protocolId}
                      />
                    )}
                  </form.AppField>
                </Box>
              </FormSection>
            </HStack>
            {protocolId ? (
              <>
                <ActiveProtocolOverlapAlert overlaps={protocolOverlaps} />
                {protocol?.notes && (
                  <Alert.Root flexDirection="column" gapY="0" width="100%">
                    <Alert.Title>{t('note')}</Alert.Title>
                    <Alert.Description>{protocol.notes}</Alert.Description>
                  </Alert.Root>
                )}
                <TherapySequence
                  protocolId={protocolId}
                  removedSequences={removedSequences}
                  onRemoveSequence={handleRemoveSequence}
                  startTime={startTime}
                />
              </>
            ) : (
              <Box display="flex" justifyContent="center" width="100%" alignItems="center" height="100%">
                <Text fontSize="md" fontWeight="semibold">
                  {t('no_protocol_selected')}
                </Text>
              </Box>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <ButtonGroup size="md" colorPalette="cyan" width="100%" justifyContent="flex-end">
            <Button variant="outline" colorPalette="gray" onClick={onClose}>
              {t('cancel')}
            </Button>
            {!canSave && (
              <Tooltip content={aucGfrRequired ? t('auc_gfr_required_tooltip') : t('characteristics_required_tooltip')}>
                {saveButton}
              </Tooltip>
            )}
            {canSave && saveButton}
          </ButtonGroup>
        </DialogFooter>
      </form.AppForm>
    </form>
  )
}

function TherapySequence({
  protocolId,
  removedSequences,
  onRemoveSequence,
  startTime,
}: {
  protocolId: string
  removedSequences: string[]
  onRemoveSequence: (id: string) => void
  startTime?: string
}) {
  const [protocol] = useGetProtocol(protocolId)

  const sequences = useMemo(() => {
    if (!protocol?.therapySequence) return []

    return protocol.therapySequence.filter(s => !removedSequences.includes(s.id))
  }, [protocol, removedSequences])

  // Mirrors the shift applied in assignPatientProtocol (engine/src/mutators/patient/assign-patient-protocol.ts):
  // every therapy's time moves by the same offset as the earliest one, relative to the chosen start time.
  const shiftedSequences = useMemo(() => {
    if (!startTime || !sequences.length) return sequences
    const earliestMinutes = Math.min(...sequences.map(s => parseTimeToMinutes(s.time)))
    const deltaMinutes = parseTimeToMinutes(startTime) - earliestMinutes
    return sequences.map(s => ({...s, time: formatMinutesToTime(parseTimeToMinutes(s.time) + deltaMinutes)}))
  }, [sequences, startTime])

  return (
    <TherapySequenceTable
      sequences={converTherapySchema(shiftedSequences)}
      onRemoveSequence={onRemoveSequence}
      showDelete
    />
  )
}
